import { Injectable, computed, effect, signal } from '@angular/core';

export type ThemePref = 'system' | 'light' | 'dark';
const KEY = 'av.theme.v1';

/**
 * Light / dark. Preference lives in localStorage ('system' follows the OS);
 * the resolved theme is applied as `html.avd-light` (dark is the default
 * palette), plus color-scheme and the theme-color meta so form controls and
 * the browser chrome follow. Header sun/moon toggles between light and dark;
 * Profile → Appearance offers System as well.
 */
@Injectable({ providedIn: 'root' })
export class ThemeService {
  readonly pref = signal<ThemePref>(readPref());
  private readonly systemDark = signal(true);
  readonly resolved = computed<'light' | 'dark'>(() => {
    const p = this.pref();
    if (p === 'system') return this.systemDark() ? 'dark' : 'light';
    return p;
  });
  readonly isLight = computed(() => this.resolved() === 'light');

  constructor() {
    try {
      const mq = window.matchMedia('(prefers-color-scheme: dark)');
      this.systemDark.set(mq.matches);
      mq.addEventListener('change', (e) => this.systemDark.set(e.matches));
    } catch {
      /* no matchMedia: stay dark */
    }
    effect(() => {
      const light = this.isLight();
      document.documentElement.classList.toggle('avd-light', light);
      document.documentElement.style.colorScheme = light ? 'light' : 'dark';
      document.querySelector('meta[name="theme-color"]')?.setAttribute('content', light ? '#f5f6f8' : '#0b0d10');
    });
  }

  set(pref: ThemePref): void {
    this.pref.set(pref);
    try {
      if (pref === 'system') localStorage.removeItem(KEY);
      else localStorage.setItem(KEY, pref);
    } catch {
      /* private mode: session only */
    }
  }

  /** Header button: flip between light and dark (leaves 'system' behind on purpose). */
  toggle(): void {
    this.set(this.isLight() ? 'dark' : 'light');
  }
}

function readPref(): ThemePref {
  try {
    const v = localStorage.getItem(KEY);
    return v === 'light' || v === 'dark' ? v : 'system';
  } catch {
    return 'system';
  }
}
